import { useState } from 'react';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import useUserSession from '../../../hooks/useUserSession';
import { useApplicationsList } from '../ApplicationsList/useApplicationsList';
import { ApplicationStatus } from '../../../shared/Constants';
import { useDashboardData } from './useDashboardData';

export const useDashboardCards = () => {
	const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(0);
	const [overallCandidates, setOverAllCandidates] = useState<boolean>(true);
	const [isZCUnpublishedRecords, setIsZCUnpublishedRecords] =
		useState<boolean>(false);
	const { selectedRole } = useUserSession();
	const { onFilterChange, filter } = useApplicationsList({});
	const isZC = selectedRole === UserRole.ZC;
	const { dashboardData } = useDashboardData();

	const handleCardClick = (index: number) => {
		setSelectedCardIndex(index);
		const selectedCard = dashboardData[index];

		if (selectedCard) {
			if (selectedCard.id !== 'overallCandidateApplications') {
				setOverAllCandidates(false);
			} else {
				setOverAllCandidates(true);
			}

			setIsZCUnpublishedRecords(
				isZC &&
					[
						'waitingForCareTeam',
						'approvedCandidates',
						'notGoingForwardCandidates',
					].includes(selectedCard?.id)
			);
		}

		if (!selectedCard) return;

		const {
			regionIds,
			zoneNames,
			applicantSrcmId,
			searchByApplicantName,
			searchByProposerName,
			searchByProposerSrcmId,
		} = filter || {};

		const isApplicationFiltered = [
			regionIds,
			zoneNames,
			applicantSrcmId,
			searchByApplicantName,
			searchByProposerName,
			searchByProposerSrcmId,
		].some(Boolean);

		type ApplicationStatusType =
			(typeof ApplicationStatus)[keyof typeof ApplicationStatus];

		const applyFilter = (status: ApplicationStatusType[]) => {
			const baseFilter = isApplicationFiltered
				? {
						regionIds,
						zoneNames,
						applicantSrcmId,
						searchByApplicantName,
						searchByProposerName,
						searchByProposerSrcmId,
				  }
				: {};

			onFilterChange({
				...baseFilter,
				status,
			});
		};

		switch (selectedCard.id) {
			case 'proposerYetToBeAssigned':
				applyFilter([ApplicationStatus.USER_SUBMITTED_APPLICATION]);
				break;

			case 'waitingForProposer':
				applyFilter([ApplicationStatus.FUNCTIONARY_ASSIGNED]);
				break;

			case 'proposerProvidedRecommendation':
				applyFilter([ApplicationStatus.FUNCTIONARY_SUBMITTED]);
				break;

			case 'waitingForCareTeam':
				applyFilter([ApplicationStatus.WAITING_FOR_CARE_TEAM]);
				break;

			case 'approvedCandidates':
				applyFilter([ApplicationStatus.APPROVED]);
				break;

			case 'notGoingForwardCandidates':
				applyFilter([ApplicationStatus.REJECTED]);
				break;

			default:
				applyFilter([
					ApplicationStatus.FUNCTIONARY_ASSIGNED,
					ApplicationStatus.FUNCTIONARY_SUBMITTED,
					ApplicationStatus.WAITING_FOR_CARE_TEAM,
					ApplicationStatus.APPROVED,
					ApplicationStatus.REJECTED,
					ApplicationStatus.USER_SUBMITTED_APPLICATION,
				]);
		}
	};

	return {
		selectedCardIndex,
		overallCandidates,
		isZCUnpublishedRecords,
		handleCardClick,
	};
};
